import styled from "styled-components";

export const PageShell = styled.div`
  max-width: 1120px;
  margin: 28px auto 56px;
  padding: 0 16px;
  color: var(--app-text, #1f1f1f);
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const TitleGroup = styled.div`
  display: grid;
  gap: 4px;
`;

export const Eyebrow = styled.div`
  color: var(--app-muted-text, #6b6b6b);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0;
`;

export const Title = styled.h2`
  margin: 0;
  color: var(--app-text, #1f1f1f);
  font-size: 28px;
  font-weight: 750;

  @media (max-width: 640px) {
    font-size: 23px;
  }
`;

export const StatusPill = styled.div`
  border: 1px solid
    ${({ $status }) => ($status === "error" ? "#d7b0ad" : "#c9c9c9")};
  color: ${({ $status }) => ($status === "error" ? "#8c1d18" : "var(--app-subtle-text, #3d3d3d)")};
  background: var(--app-surface-soft, #f7f7f7);
  border-radius: 999px;
  padding: 7px 12px;
  font-size: 13px;
  white-space: nowrap;

  @media (max-width: 640px) {
    white-space: normal;
  }
`;

export const Workspace = styled.div`
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const DocumentModeTabs = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 18px;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 9px;
  padding: 4px;
  background: var(--app-surface-soft, #f4f4f4);

  @media (max-width: 640px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }
`;

export const DocumentModeButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 0;
  border-radius: 6px;
  padding: 9px 13px;
  background: ${({ $active }) => ($active ? "var(--app-nav-active-bg, #242424)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--app-nav-active-text, #fff)" : "var(--app-subtle-text, #4f4f4f)")};
  font-weight: 750;
  cursor: pointer;

  @media (max-width: 640px) {
    justify-content: center;
  }

  svg {
    color: ${({ $active }) => ($active ? "currentColor" : "var(--app-icon-accent, #202124)")};
  }
`;

export const ControlPanel = styled.div`
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #fbfbfb);
  color: var(--app-surface-text, #1f1f1f);
  align-self: start;

  @media (max-width: 640px) {
    padding: 12px;
  }
`;

export const OutputPanel = styled.div`
  min-width: 0;
  display: grid;
  gap: 12px;
`;

export const FigureTransferWarning = styled.div`
  display: grid;
  gap: 9px;
  padding: 12px;
  border: 1px solid #d7ad53;
  border-radius: 8px;
  background: #fff8e6;
  color: #654b13;
  font-size: 14px;
  line-height: 1.5;
  > div { display: flex; gap: 8px; flex-wrap: wrap; }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: ${({ $handwriting }) => ($handwriting ? "38px" : "auto")};

  @media (max-width: 480px) {
    align-items: flex-start;
  }
`;

export const PreviewTitleGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;

  h3 {
    margin-bottom: 0;
  }
`;

export const ManualCount = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === "remove"
        ? "#e2a093"
        : $tone === "restore"
          ? "#9ac7a5"
        : $tone === "pending"
          ? "#9fb7e8"
          : "#e1b84f"};
  border-radius: 999px;
  padding: 3px 8px;
  background: ${({ $tone }) =>
    $tone === "remove"
      ? "#fff0ed"
      : $tone === "restore"
        ? "#edf8ef"
      : $tone === "pending"
        ? "#eef4ff"
        : "#fff7d6"};
  color: ${({ $tone }) =>
    $tone === "remove"
      ? "#8c2d18"
      : $tone === "restore"
        ? "#1f6f35"
      : $tone === "pending"
        ? "#214f9a"
        : "#6b4e00"};
  font-size: 12px;
  font-weight: 750;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 10px;
  font-size: 15px;
  font-weight: 750;
`;

export const ModeGrid = styled.div`
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
`;

export const ModeButton = styled.button`
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr);
  align-items: center;
  gap: 4px 10px;
  border: 1px solid ${({ $active }) => ($active ? "var(--app-nav-active-bg, #242424)" : "var(--app-border, #d6d6d6)")};
  border-radius: 8px;
  padding: 11px;
  background: ${({ $active }) => ($active ? "var(--app-nav-active-bg, #242424)" : "var(--app-input-bg, #fff)")};
  color: ${({ $active }) => ($active ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #262626)")};
  text-align: left;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  svg {
    grid-row: span 2;
    color: ${({ $active }) => ($active ? "currentColor" : "var(--app-icon-accent, #202124)")};
    font-size: 19px;
  }

  span {
    font-weight: 800;
  }

  small {
    color: ${({ $active }) => ($active ? "var(--app-nav-active-text, #e4e4e4)" : "var(--app-muted-text, #6a6a6a)")};
    line-height: 1.45;
  }
`;

export const DropZone = styled.div`
  display: grid;
  place-items: center;
  gap: 8px;
  min-height: 170px;
  border: 2px dashed ${({ $dragOver }) => ($dragOver ? "var(--app-nav-active-bg, #242424)" : "var(--app-border, #bdbdbd)")};
  background: ${({ $dragOver }) => ($dragOver ? "var(--app-surface-soft, #eeeeee)" : "var(--app-surface-hover, #f6f6f6)")};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  color: var(--app-muted-text, #6b6b6b);

  svg {
    color: var(--app-icon-accent, #202124);
    font-size: 28px;
  }

  strong {
    color: var(--app-surface-text, #1f1f1f);
    max-width: 100%;
    overflow-wrap: anywhere;
  }

  @media (max-width: 640px) {
    min-height: 132px;
    padding: 16px;
  }
`;

export const HiddenInput = styled.input`
  display: none;
`;

export const HandoutFileList = styled.ol`
  display: grid;
  gap: 7px;
  max-height: 190px;
  margin: 10px 0 0;
  padding: 0;
  overflow-y: auto;
  list-style: none;

  li {
    display: grid;
    grid-template-columns: 26px minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border: 1px solid var(--app-border, #d6d6d6);
    border-radius: 7px;
    background: var(--app-input-bg, #fff);
  }

  li > span {
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--app-surface-soft, #eceff3);
    color: var(--app-surface-text, #242424);
    font-size: 12px;
    font-weight: 800;
  }

  strong {
    min-width: 0;
    overflow: hidden;
    color: var(--app-surface-text, #242424);
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    color: var(--app-muted-text, #6a6a6a);
    white-space: nowrap;
  }
`;

export const PrimaryButton = styled.button`
  width: 100%;
  margin-top: 14px;
  border: 0;
  border-radius: 8px;
  padding: 12px 14px;
  background: #242424;
  color: #fff;
  font-weight: 750;
  cursor: pointer;

  &:disabled {
    background: #8a8a8a;
    cursor: progress;
  }
`;

export const SecondaryFullButton = styled.button`
  width: 100%;
  margin-top: 10px;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  padding: 11px 14px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #242424);
  font-weight: 750;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    color: #777;
    background: var(--app-surface-soft, #eeeeee);
    cursor: not-allowed;
  }
`;

export const FieldStack = styled.div`
  display: grid;
  gap: 7px;
  margin-bottom: 12px;
`;

export const FieldLabel = styled.label`
  color: var(--app-subtle-text, #4f4f4f);
  font-size: 13px;
  font-weight: 750;
`;

export const SelectInput = styled.select`
  width: 100%;
  min-height: 40px;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  padding: 0 10px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #1f1f1f);
  font: inherit;
  font-weight: 650;

  &:disabled {
    color: var(--app-muted-text, #8a8a8a);
    background: var(--app-surface-soft, #f5f5f5);
  }
`;

export const RangeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const RangeValue = styled.span`
  min-width: 26px;
  border-radius: 999px;
  padding: 3px 8px;
  background: var(--app-surface-soft, #eeeeee);
  color: var(--app-subtle-text, #3d3d3d);
  font-size: 12px;
  font-weight: 750;
  text-align: center;
`;

export const RangeInput = styled.input`
  width: 100%;
  accent-color: #242424;
  height: 6px;
  border-radius: 999px;
  cursor: pointer;
`;

export const StrengthStatus = styled.span`
  color: var(--app-muted-text, #666);
  font-size: 12px;
  line-height: 1.5;
`;

export const CleanButton = styled.button`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px solid #242424;
  border-radius: 8px;
  padding: 11px 14px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #242424);
  font-weight: 750;
  cursor: pointer;

  & + & {
    margin-top: 8px;
  }

  ${({ $loading }) =>
    $loading
      ? `
        cursor: progress;
        pointer-events: none;
        opacity: 0.78;

        svg {
          animation: handwriting-spin 0.9s linear infinite;
        }
      `
      : ""}

  &:disabled {
    border-color: #9a9a9a;
    color: #777;
    background: var(--app-input-bg, #fff);
    cursor: ${({ $loading }) => ($loading ? "progress" : "not-allowed")};
  }

  @keyframes handwriting-spin {
    to { transform: rotate(360deg); }
  }
`;

export const CleanupOutputBox = styled.div`
  min-height: 230px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  border: 1px solid
    ${({ $status }) => ($status === "error" ? "#d7b0ad" : "var(--app-border, #d8d8d8)")};
  border-radius: 8px;
  padding: 24px;
  background: ${({ $status }) =>
    $status === "success" ? "#f1f8f3" : "var(--app-surface, #fbfbfb)"};
  color: var(--app-subtle-text, #4f4f4f);
  text-align: center;

  svg {
    color: ${({ $status }) => ($status === "success" ? "#34a853" : "var(--app-icon-accent, #202124)")};
    font-size: 36px;
  }

  strong {
    max-width: 100%;
    color: var(--app-surface-text, #1f1f1f);
    overflow-wrap: anywhere;
  }
`;

export const PreviewLoadingFrame = styled.div`
  position: relative;
  min-height: 120px;
  ${({ $loading }) => ($loading ? "cursor: progress;" : "")}
`;

export const PreviewViewport = styled.div`
  max-height: 78vh;
  overflow: auto;
  overscroll-behavior: contain;
  border-radius: 8px;
`;

export const ZoomGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  padding: 4px;
  color: var(--app-subtle-text, #4f4f4f);
  font-size: 12px;
  span { min-width: 42px; text-align: center; }
  button:disabled { opacity: .4; cursor: not-allowed; }
`;

export const PreviewLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 10px;
  background: color-mix(in srgb, var(--app-surface, #fbfbfb) 78%, transparent);
  color: var(--app-subtle-text, #3d3d3d);
  font-weight: 750;
  cursor: progress;
`;

export const PreviewLoadingIndicator = styled.div`
  position: sticky;
  top: 100px;
  display: grid;
  justify-items: center;
  gap: 10px;
  margin: 32px 16px;
  padding: 16px 20px;
  max-width: calc(100% - 32px);
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  background: var(--app-surface, #fbfbfb);
  text-align: center;

  svg {
    font-size: 28px;
    animation: handwriting-preview-spin 0.9s linear infinite;
  }

  @keyframes handwriting-preview-spin {
    to { transform: rotate(360deg); }
  }
`;

export const ManualControls = styled.div`
  position: relative;
  z-index: 5;
  display: grid;
  gap: 8px;
  padding: 8px 0;
  background: var(--app-surface, #fbfbfb);
  ${({ $modal }) => $modal ? "flex-shrink: 0; max-height: 45dvh; overflow: auto;" : ""}
`;

export const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  width: 100%;
  max-width: 100%;
  gap: 8px;
`;

export const PreviewStatusBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid ${({ $status }) => ($status === "error" ? "#e2b6b0" : "var(--app-border, #d8d8d8)")};
  border-radius: 8px;
  padding: 9px 10px;
  background: ${({ $status }) =>
    $status === "loading" ? "var(--app-surface-soft, #f4f6f8)" : $status === "error" ? "#fff4f2" : "var(--app-surface, #fbfbfb)"};
  color: ${({ $status }) => ($status === "error" ? "#9b2c1d" : "var(--app-subtle-text, #3d3d3d)")};
  font-size: 13px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const PreviewStats = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: var(--app-muted-text, #666);
  font-size: 12px;

  span {
    border-radius: 999px;
    padding: 3px 7px;
    background: var(--app-surface-soft, #eeeeee);
    white-space: nowrap;
  }
`;

export const ViewModeGroup = styled.div`
  display: inline-flex;
  gap: 4px;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  padding: 4px;
  background: var(--app-surface-soft, #f4f4f4);
`;

export const ViewModeButton = styled.button`
  border: 0;
  border-radius: 5px;
  padding: 7px 10px;
  background: ${({ $active }) => ($active ? "var(--app-nav-active-bg, #242424)" : "transparent")};
  color: ${({ $active }) => ($active ? "var(--app-nav-active-text, #fff)" : "var(--app-subtle-text, #4f4f4f)")};
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
  white-space: nowrap;
`;

export const ManualToolGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

export const ConfirmMaskButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid ${({ $active }) => ($active ? "#137333" : "#242424")};
  border-radius: 8px;
  padding: 9px 11px;
  background: ${({ $active }) => ($active ? "#137333" : "#242424")};
  color: #fff;
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    border-color: #b5b5b5;
    background: #efefef;
    color: #8a8a8a;
    cursor: not-allowed;
  }
`;

export const ManualToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border: 1px solid
    ${({ $active, $tone }) =>
      $active ? ($tone === "restore" ? "#24864a" : "#2f6fd6") : "#c9c9c9"};
  border-radius: 8px;
  padding: 9px 11px;
  background: ${({ $active, $tone }) =>
    $active ? ($tone === "restore" ? "#1f7a43" : "#1f5fc1") : "var(--app-input-bg, #fff)"};
  color: ${({ $active }) => ($active ? "#fff" : "var(--app-input-text, #3d3d3d)")};
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const SelectionActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  border: 1px solid ${({ $tone }) => ($tone === "restore" ? "#c9dfcf" : "#d2dbe9")};
  border-radius: 8px;
  padding: 10px;
  background: ${({ $tone }) => ($tone === "restore" ? "#f3faf5" : "#f6f9ff")};
`;

export const SelectionText = styled.span`
  margin-right: 2px;
  color: ${({ $tone }) => ($tone === "restore" ? "#216236" : "#31517f")};
  font-size: 13px;
  font-weight: 750;
`;

export const ManualHint = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px 12px;
  padding: 8px 10px;
  border-radius: 7px;
  background: ${({ $tone }) => ($tone === "restore" ? "#edf8ef" : "#eef4ff")};
  color: ${({ $tone }) => ($tone === "restore" ? "#216236" : "#31517f")};
  font-size: 12px;
  font-weight: 650;
  small { font-weight: 400; }
`;

export const SelectionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid
    ${({ $tone }) =>
      $tone === "remove"
        ? "#c9533c"
        : $tone === "restore"
          ? "#2d8a4f"
          : "#b9c2d0"};
  border-radius: 7px;
  padding: 8px 10px;
  background: ${({ $tone }) =>
    $tone === "remove" ? "#fff4f1" : $tone === "restore" ? "#eef9f1" : "var(--app-input-bg, #fff)"};
  color: ${({ $tone }) =>
    $tone === "remove" ? "#8c2d18" : $tone === "restore" ? "#1f6f35" : "#3f4b5b"};
  font-size: 13px;
  font-weight: 750;
  cursor: pointer;
  &:disabled { opacity: .45; cursor: not-allowed; }
`;

export const PreviewList = styled.div`
  display: grid;
  gap: 14px;
`;

export const PreviewPageCard = styled.div`
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  background: var(--app-surface, #fbfbfb);
  overflow: hidden;
`;

export const PreviewPageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid var(--app-border, #e3e3e3);
  background: var(--app-surface-hover, #fff);

  strong {
    color: var(--app-surface-text, #1f1f1f);
  }

  span {
    color: var(--app-muted-text, #666);
    font-size: 13px;
    white-space: nowrap;
  }
`;

export const ImageOverlayControls = styled.div`
  display: flex; flex-wrap: wrap; align-items: center; gap: 10px;
  padding: 10px 0; color: var(--app-text, #303134); font-size: 13px;
  select { padding: 4px; }
  small { color: var(--app-muted-text, #666); }
  [role="status"] { flex-basis: 100%; }
`;
export const RepairControls = styled.div`
  display: grid; gap: 8px; width: 100%; padding: 12px; border: 1px solid var(--app-border, #ccc); border-radius: 8px;
  label { display: grid; gap: 6px; }
  textarea { width: 100%; box-sizing: border-box; resize: vertical; padding: 8px;
    border: 1px solid var(--app-border, #bbb); border-radius: 6px;
    background: var(--app-input-bg, #fff); color: var(--app-input-text, #222); }
  button { justify-self: start; }
`;

export const PreviewCanvas = styled.div`
  position: relative;
  width: 100%;
  background: #e8e8e8;
  touch-action: ${({ $manualActive }) => ($manualActive ? "none" : "pan-x pan-y pinch-zoom")};
  user-select: none;
  cursor: ${({ $manualActive, $busy, $expandable }) => ($busy ? "progress" : $manualActive ? "crosshair" : $expandable ? "zoom-in" : "default")};
  outline: ${({ $manualActive, $toolMode }) =>
    $manualActive
      ? `3px solid ${
        $toolMode === "restore"
          ? "rgba(36, 134, 74, 0.85)"
          : "rgba(47, 111, 214, 0.85)"
      }`
      : "0 solid transparent"};
  outline-offset: -3px;
`;

export const ManualResultRegion = styled.div`
  position: absolute;
  overflow: hidden;
  background: #fff;
  pointer-events: none;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: ${({ $maskErase }) => $maskErase ? "rgba(255, 0, 0, 0.4705882353)" : "transparent"};
  }

  img {
    position: absolute;
    display: block;
    max-width: none;
    pointer-events: none;
  }
`;

export const PreviewImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  pointer-events: none;
`;

export const PreviewMask = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

export const ManualRegionBox = styled.div`
  position: absolute;
  box-sizing: border-box;
  border:
    ${({ $tone }) =>
      $tone === "remove"
        ? "0"
        : $tone === "restore"
          ? "2px solid #24864a"
        : $tone === "pending"
          ? "1px solid #2f6fd6"
          : "2px solid #f5b400"};
  background: ${({ $tone }) =>
    $tone === "remove"
      ? "#fff"
      : $tone === "restore"
        ? "rgba(255, 255, 255, 0)"
      : $tone === "pending"
        ? "rgba(47, 111, 214, 0.12)"
        : "rgba(47, 111, 214, 0.12)"};
  box-shadow:
    ${({ $tone }) =>
      $tone === "remove"
        ? "none"
        : $tone === "restore"
          ? "0 0 0 1px rgba(31, 111, 53, 0.25)"
        : $tone === "pending"
          ? "0 0 0 1px rgba(24, 70, 144, 0.25)"
          : "0 0 0 1px rgba(80, 52, 0, 0.25)"};
  pointer-events: none;
`;

export const DownloadLink = styled.a`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  border-radius: 8px;
  padding: 10px 12px;
  background: #e8f0fe;
  color: #174ea6;
  font-weight: 750;
  text-decoration: none;
`;

export const SecondaryButton = styled.button`
  border: 1px solid var(--app-border, #bdbdbd);
  border-radius: 8px;
  padding: 9px 12px;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #1f1f1f);
  font-weight: 700;
  cursor: pointer;
`;

export const PrimaryInlineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  padding: 9px 14px;
  background: #242424;
  color: #fff;
  font-weight: 750;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: #8a8a8a;
    cursor: progress;
  }
`;

export const IconButton = styled.button`
  width: 38px;
  height: 38px;
  display: inline-grid;
  place-items: center;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 8px;
  background: var(--app-input-bg, #fff);
  color: var(--app-icon-accent, #202124);
  cursor: pointer;
  flex: 0 0 auto;

  &:disabled {
    color: #999;
    cursor: not-allowed;
  }
`;

export const IconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const Message = styled.div`
  margin-top: 12px;
  color: ${({ $status }) => ($status === "error" ? "#b3261e" : "#137333")};
  font-size: 14px;
`;

export const AiProgressPanel = styled.div`
  display: grid;
  gap: 12px;
  border: 1px solid
    ${({ $status }) => {
      if ($status === "error") return "#f0c7c1";
      if ($status === "success") return "#b8ddc3";
      return "rgba(37, 99, 235, 0.18)";
    }};
  border-radius: 8px;
  padding: 14px;
  background: ${({ $status }) => {
    if ($status === "error") return "var(--app-danger-bg, #fff7f5)";
    if ($status === "success") return "var(--app-surface, #f5fbf7)";
    return "var(--app-input-bg, #ffffff)";
  }};
  color: var(--app-input-text, #303134);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.07);
`;

export const AiProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const AiProgressTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  svg {
    flex: 0 0 auto;
    color: #2563eb;
  }

  strong {
    color: var(--app-text, #202124);
    font-size: 15px;
    line-height: 1.2;
  }

  ${AiProgressPanel}[data-status="loading"] & svg {
    animation: spin 0.9s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const AiProgressMeta = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--app-muted-text, #64748b);
  font-size: 12px;
  font-weight: 800;
  white-space: nowrap;

  span {
    border: 1px solid var(--app-border, rgba(100, 116, 139, 0.22));
    border-radius: 999px;
    padding: 4px 8px;
    background: var(--app-surface-soft, #f8fafc);
    color: var(--app-surface-text, #334155);
  }

  @media (max-width: 640px) {
    white-space: normal;
  }
`;

export const AiProgressTrack = styled.div`
  height: 8px;
  overflow: hidden;
  border-radius: 999px;
  background: var(--app-surface-soft, #e8eef7);

  span {
    display: block;
    height: 100%;
    min-width: 6px;
    border-radius: inherit;
    background: ${({ $active }) =>
      $active
        ? "linear-gradient(90deg, #2563eb 0%, #14b8a6 50%, #2563eb 100%)"
        : "#2563eb"};
    transition: width 180ms ease;
    ${({ $indeterminate }) =>
      $indeterminate
        ? `
          animation: aiProgressPulse 1.1s ease-in-out infinite;
          transform-origin: left center;
        `
        : ""}
  }

  @keyframes aiProgressPulse {
    0% {
      transform: translateX(-40%) scaleX(0.75);
    }
    50% {
      transform: translateX(230%) scaleX(1.25);
    }
    100% {
      transform: translateX(560%) scaleX(0.75);
    }
  }
`;

export const AiProgressFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  color: var(--app-muted-text, #64748b);
  font-size: 13px;
  line-height: 1.45;

  span {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const RetryButton = styled.button`
  flex: 0 0 auto;
  border: 1px solid #8c2d18;
  border-radius: 8px;
  padding: 7px 11px;
  background: #fff;
  color: #8c2d18;
  font-weight: 800;
  cursor: pointer;
`;

export const Diagnostics = styled.div`
  margin-top: 12px;
  display: grid;
  gap: 4px;
  border: 1px solid var(--app-border, #d7d7d7);
  border-radius: 8px;
  padding: 10px;
  background: var(--app-surface-soft, #f4f4f4);
  color: var(--app-subtle-text, #3d3d3d);
  font-size: 13px;
`;

export const TextArea = styled.textarea`
  width: 100%;
  min-height: 250px;
  resize: vertical;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  padding: 14px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 14px;
  line-height: 1.6;
  background: var(--app-input-bg, #fff);
  color: var(--app-input-text, #202124);

  @media (max-width: 640px) {
    min-height: 190px;
    font-size: 13px;
  }
`;

export const AiChatWorkspace = styled.div`
  display: grid;
  min-height: min(760px, calc(100vh - 250px));
`;

export const AiChatPanel = styled.div`
  display: grid;
  grid-template-rows: auto auto minmax(280px, 1fr) auto auto;
  gap: 12px;
  min-height: 0;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 14px;
  background: var(--app-surface, #fff);
  color: var(--app-text, #202124);
`;

export const AiChatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid var(--app-border, #eeeeee);
  padding-bottom: 12px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const AiChatTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  > svg {
    width: 24px;
    height: 24px;
    color: var(--app-icon-accent, #202124);
    flex: 0 0 auto;
  }

  div {
    display: grid;
    gap: 2px;
    min-width: 0;
  }

  strong {
    font-size: 18px;
    font-weight: 850;
    line-height: 1.2;
  }

  span {
    color: var(--app-muted-text, #666);
    font-size: 13px;
    font-weight: 700;
  }
`;

export const AiChatSettingsBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

export const AiChatSolutionActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;

  @media (max-width: 760px) {
    width: 100%;

    ${PrimaryInlineButton} {
      flex: 1 1 auto;
    }
  }
`;

export const AiChatStatus = styled(Message)`
  margin: 0 0 4px;
  min-height: 20px;
  flex: 1 1 180px;
`;

export const AiChatMessages = styled.div`
  display: grid;
  align-content: start;
  gap: 16px;
  min-height: 0;
  overflow: auto;
  border: 1px solid var(--app-border, #e5e7eb);
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface-soft, #f8fafc);
`;

export const AiChatMessage = styled.div`
  display: grid;
  justify-items: ${({ $role }) => ($role === "user" ? "end" : "start")};
  gap: 6px;
`;

export const ChatMessageLabel = styled.div`
  color: var(--app-muted-text, #666);
  font-size: 12px;
  font-weight: 850;
`;

export const ChatMessageBubble = styled.div`
  width: ${({ $role }) => ($role === "user" ? "min(720px, 88%)" : "min(900px, 100%)")};
  display: grid;
  gap: 10px;
  border: 1px solid
    ${({ $role }) =>
      $role === "user" ? "var(--app-nav-active-bg, #242424)" : "rgba(148, 163, 184, 0.28)"};
  border-radius: 8px;
  padding: 12px;
  background: ${({ $role }) =>
    $role === "user" ? "var(--app-nav-active-bg, #242424)" : "var(--app-input-bg, #ffffff)"};
  color: ${({ $role }) => ($role === "user" ? "var(--app-nav-active-text, #fff)" : "var(--app-input-text, #202124)")};
  box-shadow: ${({ $role }) =>
    $role === "user" ? "none" : "0 8px 22px rgba(0, 0, 0, 0.06)"};

  @media (max-width: 640px) {
    width: 100%;
  }
`;

export const ChatAttachmentSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  > div {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.28);
    color: inherit;

    button,
    svg {
      color: inherit;
    }
  }
`;

export const ChatPromptPreview = styled.div`
  display: grid;
  gap: 10px;
  color: ${({ $muted }) => ($muted ? "rgba(255, 255, 255, 0.7)" : "inherit")};
  font-size: 14px;
  font-weight: 650;
  line-height: 1.7;
  overflow-wrap: anywhere;
`;

export const ChatTextSegment = styled.div`
  white-space: pre-wrap;
`;

export const ChatMarkdownParagraph = styled.p`
  margin: 0;
  white-space: pre-wrap;
`;

export const ChatMarkdownHeading = styled.h2`
  margin: 0;
  font-size: ${({ $level }) => `${Math.max(1, 1.45 - ($level - 1) * 0.1)}em`};
  line-height: 1.35;
  font-weight: 850;
`;

export const ChatMarkdownList = styled.ol`
  margin: 0;
  padding-left: 24px;
  display: grid;
  gap: 6px;
`;

export const ChatMarkdownStrong = styled.strong`
  font-weight: 850;
`;

export const ChatInlineCode = styled.code`
  padding: 2px 5px;
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 4px;
  background: rgba(148, 163, 184, 0.14);
  color: #f59e0b;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.92em;
  font-weight: 700;

  [data-auth-theme="night"] & {
    border-color: var(--app-border, #555555);
    background: var(--app-surface-soft, #333333);
  }
`;

export const CodeBlockFrame = styled.figure`
  min-width: 0;
  overflow: hidden;
  margin: 2px 0;
  border: 1px solid var(--app-code-border, #263241);
  border-radius: 8px;
  background: var(--app-code-bg, #111827);
  color: var(--app-code-text, #e5e7eb);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

export const CodeBlockHeader = styled.figcaption`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  padding: 7px 9px 7px 11px;
  background: var(--app-code-header-bg, #0b1220);
  color: #cbd5e1;
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0;

  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const CodeCopyButton = styled.button`
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid rgba(203, 213, 225, 0.2);
  border-radius: 6px;
  padding: 4px 8px;
  background: rgba(248, 250, 252, 0.08);
  color: #f8fafc;
  font-size: 12px;
  font-weight: 800;
  cursor: pointer;

  svg {
    font-size: 12px;
  }

  &:hover,
  &:focus-visible {
    border-color: rgba(56, 189, 248, 0.6);
    background: rgba(56, 189, 248, 0.16);
    outline: none;
  }
`;

export const CodeScroll = styled.div`
  max-width: 100%;
  overflow-x: auto;
`;

export const CodePre = styled.pre`
  min-width: max-content;
  margin: 0;
  padding: 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace;
  font-size: 13px;
  font-weight: 650;
  line-height: 1.65;
`;

export const CodeLine = styled.div`
  min-height: 1.65em;
  white-space: pre;
`;

export const CodeToken = styled.span`
  color: ${({ $kind }) => {
    if ($kind === "comment") return "#94a3b8";
    if ($kind === "keyword") return "#93c5fd";
    if ($kind === "number") return "#fbbf24";
    if ($kind === "operator") return "#c4b5fd";
    if ($kind === "string") return "#86efac";
    if ($kind === "tag") return "#fca5a5";
    return "inherit";
  }};
`;

export const AiChatEmpty = styled.div`
  display: grid;
  gap: 4px;
  color: var(--app-subtle-text, #3d3d3d);

  strong {
    color: var(--app-input-text, #202124);
    font-size: 15px;
  }

  span {
    color: var(--app-muted-text, #666);
    font-size: 13px;
    font-weight: 700;
  }
`;

export const AiChatSolutionPanel = styled.section`
  display: grid;
  gap: 12px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-surface-soft, #f8fafc);
`;

export const AiChatSolutionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  @media (max-width: 560px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const AiChatSolutionTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--app-input-text, #202124);

  svg {
    color: var(--app-icon-accent, #202124);
  }

  strong {
    font-size: 15px;
    font-weight: 850;
  }
`;

export const ChatComposer = styled.div`
  display: grid;
  gap: 10px;
  border: 1px solid
    ${({ $dragOver }) => ($dragOver ? "var(--app-nav-active-bg, #242424)" : "var(--app-border, #d8d8d8)")};
  border-radius: 8px;
  padding: 12px;
  background: ${({ $dragOver }) => ($dragOver ? "var(--app-surface-soft, #f1f3f4)" : "var(--app-surface, #fff)")};
  box-shadow: ${({ $dragOver }) =>
    $dragOver ? "inset 0 0 0 2px rgba(32, 33, 36, 0.12)" : "none"};
`;

export const ComposerAttachmentList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const ComposerAttachmentChip = styled.div`
  max-width: min(100%, 260px);
  display: inline-grid;
  grid-template-columns: 16px minmax(0, 1fr) 24px;
  align-items: center;
  gap: 7px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 6px 6px 6px 9px;
  background: var(--app-surface-soft, #f4f4f4);
  color: var(--app-subtle-text, #3d3d3d);
  font-size: 12px;
  font-weight: 750;

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  button {
    width: 24px;
    height: 24px;
    display: inline-grid;
    place-items: center;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--app-icon-accent, #202124);
    cursor: pointer;
  }
`;

export const ComposerTextarea = styled.textarea`
  width: 100%;
  min-height: 150px;
  resize: vertical;
  border: 0;
  border-radius: 6px;
  padding: 10px 2px;
  background: transparent;
  color: var(--app-input-text, #202124);
  font: inherit;
  line-height: 1.6;
  outline: none;
`;

export const ComposerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-top: 1px solid var(--app-border, #eeeeee);
  padding-top: 10px;

  @media (max-width: 560px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const ComposerHint = styled.span`
  color: var(--app-muted-text, #666);
  font-size: 12px;
  font-weight: 700;
`;

export const ComposerActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
`;

export const ComposerIconButton = styled.button`
  width: 38px;
  height: 38px;
  display: inline-grid;
  place-items: center;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 8px;
  background: var(--app-input-bg, #fff);
  color: var(--app-icon-accent, #202124);
  cursor: pointer;
`;

export const ComposerSendButton = styled.button`
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  padding: 0 13px;
  background: #242424;
  color: #fff;
  font-weight: 800;
  cursor: pointer;
  white-space: nowrap;

  &:disabled {
    background: #8a8a8a;
    cursor: progress;
  }
`;

export const ParsedQuestionPanel = styled.div`
  display: grid;
  gap: 10px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-surface, #fff);
`;

export const ParsedQuestionMeta = styled.div`
  margin-top: 2px;
  color: var(--app-muted-text, #6b6b6b);
  font-size: 12px;
  font-weight: 750;
`;

export const ParsedSectionList = styled.div`
  display: grid;
  gap: 12px;
`;

export const ParsedSectionCard = styled.section`
  display: grid;
  gap: 8px;
  min-width: 0;
`;

export const ParsedSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  border-bottom: 1px solid var(--app-border, #e6e6e6);
  padding-bottom: 7px;
  color: var(--app-subtle-text, #3d3d3d);
  font-size: 13px;

  span {
    color: var(--app-muted-text, #6b6b6b);
    font-size: 12px;
    font-weight: 750;
  }
`;

export const ParsedQuestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 8px;
`;

export const ParsedQuestionCard = styled.article`
  min-width: 0;
  display: grid;
  gap: 8px;
  border: 1px solid var(--app-border, #e2e4e7);
  border-radius: 8px;
  padding: 10px;
  background: var(--app-surface-soft, #f7f7f7);
`;

export const ParsedQuestionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: var(--app-text, #202124);
  font-size: 13px;
`;

export const ParsedWarning = styled.span`
  border: 1px solid #e2a093;
  border-radius: 999px;
  padding: 2px 7px;
  background: #fff0ed;
  color: #8c2d18;
  font-size: 11px;
  font-weight: 800;
`;

export const ParsedQuestionStem = styled.div`
  max-height: 130px;
  overflow: auto;
  color: var(--app-input-text, #202124);
  font-size: 13px;
  line-height: 1.55;
`;

export const ParsedOptionList = styled.ul`
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: var(--app-subtle-text, #3d3d3d);
  font-size: 12px;

  li {
    display: flex;
    gap: 5px;
    min-width: 0;
  }
`;

export const ParsedQuestionFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 5px;

  span {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    border: 1px solid var(--app-border, #d7dce0);
    border-radius: 999px;
    padding: 3px 7px;
    background: var(--app-input-bg, #fff);
    color: var(--app-muted-text, #5f6368);
    font-size: 11px;
    font-weight: 800;
  }
`;

export const MatplotlibPreviewPanel = styled.div`
  display: grid;
  gap: 10px;
  border: 1px solid var(--app-border, #d8d8d8);
  border-radius: 8px;
  padding: 12px;
  background: var(--app-surface, #fff);
`;

export const MatplotlibModalPreview = styled.div`
  min-height: 100%;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 10px;
`;

export const MatplotlibPreviewFrame = styled.div`
  position: relative;
  min-height: 220px;
  display: grid;
  place-items: stretch;
  border-radius: 8px;

  ${({ $loading, $pending }) =>
    $loading || $pending
      ? `
        cursor: progress;
      `
      : ""}
`;

export const MatplotlibPreviewEmpty = styled.div`
  min-height: 180px;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 10px;
  border: 1px dashed var(--app-border, #cfcfcf);
  border-radius: 8px;
  background: ${({ $loading }) =>
    $loading
      ? "linear-gradient(135deg, var(--app-surface-soft, #f7f7f7), var(--app-surface, #ffffff))"
      : "var(--app-surface-soft, #f7f7f7)"};
  color: ${({ $loading }) =>
    $loading ? "var(--app-subtle-text, #3d3d3d)" : "var(--app-muted-text, #6b6b6b)"};
  text-align: center;
  font-weight: 750;

  svg {
    color: var(--app-icon-accent, #202124);
    font-size: 28px;
    ${({ $loading }) =>
      $loading
        ? `
          animation: spin 0.9s linear infinite;
        `
        : ""}
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const MatplotlibLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  align-content: center;
  gap: 8px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.72);
  color: var(--app-subtle-text, #303134);
  font-size: 13px;
  font-weight: 850;
  backdrop-filter: blur(2px);

  svg {
    font-size: 24px;
    animation: spin 0.9s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const DraftActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;

  @media (max-width: 640px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const DraftButtonGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;

export const SolutionModelPicker = styled.div`
  width: min(100%, 560px);
  display: grid;
  gap: 4px;
  flex: 1 1 420px;
  min-width: 300px;
`;

export const SolutionModelLabel = styled.div`
  color: var(--app-subtle-text, #4f4f4f);
  font-size: 13px;
  font-weight: 800;
`;

export const SolutionModelOptions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px;
  padding: 3px;
  border: 1px solid var(--app-border, #cfcfcf);
  border-radius: 8px;
  background: var(--app-surface-soft, #f4f4f4);
`;

export const SolutionModelOption = styled.button`
  min-height: 42px;
  display: grid;
  align-content: center;
  gap: 1px;
  border: 1px solid
    ${({ $active }) => ($active ? "var(--app-nav-active-bg, #242424)" : "transparent")};
  border-radius: 6px;
  padding: 5px 10px;
  background: ${({ $active }) =>
    $active ? "var(--app-input-bg, #fff)" : "transparent"};
  color: var(--app-input-text, #202124);
  text-align: left;
  cursor: pointer;

  strong {
    font-size: 14px;
    font-weight: 850;
    line-height: 1.15;
  }

  span {
    color: var(--app-muted-text, #6b6b6b);
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    overflow-wrap: anywhere;
  }

  &:disabled {
    cursor: progress;
    opacity: 0.72;
  }

  @media (max-width: 760px) {
    min-height: 40px;
    padding: 5px 8px;
  }
`;

export const SolutionActionGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0 0 auto;
  align-self: center;
  margin-top: 17px;

  @media (max-width: 760px) {
    width: 100%;
    margin-top: 0;

    ${PrimaryInlineButton},
    ${SecondaryButton} {
      flex: 1 1 0;
    }
  }

  @media (max-width: 460px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

export const HandwritingModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));

  @media (max-width: 640px) { padding: 8px; }
`;

export const HandwritingModal = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: min(1440px, 100%);
  height: calc(100dvh - 32px);
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  padding: 16px;
  border: 1px solid var(--app-border, #c9c9c9);
  border-radius: 12px;
  background: var(--app-surface, #fbfbfb);
  color: var(--app-surface-text, #202124);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.3);

  @media (max-width: 640px) {
    height: calc(100dvh - 16px);
    padding: 10px;
  }
`;

export const HandwritingModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  gap: 16px;

  > div { display: flex; align-items: baseline; gap: 12px; min-width: 0; }
  strong { white-space: nowrap; }
  small { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--app-muted-text, #666); }
`;

export const HandwritingModalViewport = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  border-radius: 8px;
  background: #d9d9d9;
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: grid;
  place-items: center;
  padding: 18px;
  background: var(--app-modal-overlay, rgba(64, 64, 64, 0.72));
`;

export const JsonDialog = styled.div`
  width: min(560px, 100%);
  display: grid;
  gap: 14px;
  border-radius: 8px;
  padding: 16px;
  background: var(--app-surface, #fff);
  color: var(--app-surface-text, #202124);
  box-shadow: 0 18px 46px rgba(0, 0, 0, 0.22);
`;

export const DialogFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const DialogActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

export const CopyToast = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1200;
  border-radius: 8px;
  padding: 10px 14px;
  background: #242424;
  color: #fff;
  font-size: 14px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);

  @media (max-width: 640px) {
    right: 12px;
    bottom: 12px;
  }
`;
